<script setup lang="ts">
import { computed } from 'vue'

/**
 * A column of blocks with a gap between them — the vertical half of `Inline`.
 *
 * Reach for it instead of writing `display: flex; flex-direction: column` plus a `gap`
 * under a class whose only job is to hold them. `gap` defaults to 16 — this app's rhythm
 * between page blocks — so `<Stack>` with no props is already the common case; `Inline`
 * defaults to 0 because a row has no such rhythm.
 */
const props = withDefaults(
  defineProps<{
    /** Gap between children: px as a number, or any CSS length. */
    gap?: number | string
    align?: 'stretch' | 'start' | 'center' | 'end'
    block?: boolean
  }>(),
  { gap: 16, align: 'stretch', block: false }
)

const ALIGN = {
  stretch: 'stretch',
  start: 'flex-start',
  center: 'center',
  end: 'flex-end'
}

const style = computed(() => ({
  gap: typeof props.gap === 'number' ? `${props.gap}px` : props.gap,
  alignItems: ALIGN[props.align],
  width: props.block ? '100%' : undefined
}))
</script>

<template>
  <div class="shared-stack" :style="style">
    <slot />
  </div>
</template>

<style lang="scss" scoped>
.shared-stack {
  display: flex;
  flex-direction: column;
}
</style>
